/**
 * What stops a Test, Load, Save or Save anyway (Option C, C0): the first applicable field error in screen order, then
 * the form-level issues no single field owns. Replaces `connectionValidationFocus.ts` and its selector map, which
 * missed the SSH host and port. Pure: no DOM, no React.
 */
import type { ConnectionFormValues } from '../../components/connectionFormSchema';
import { getConnectionFieldDefault } from '../../components/connectionFieldDefaults';
import { storageEnabledServices } from '../../components/azureStorageFormRules';
import { SHARE_PATH_REQUIRED } from '../../components/azureStorageSharePath';
import { FIELD_RULES, type FieldRuleId } from './connectionFieldRules';

type Values = Partial<ConnectionFormValues>;
type FieldMeta = Record<string, { errors?: unknown[] } | undefined>;

/** Shown once, under Services (final UX review UX-04: it used to show in the result strip too). */
export const STORAGE_SERVICES_REQUIRED = 'Tick at least one service to test or save.';

/** An issue of the form as a whole. It is not a zod `superRefine`, so `connectionFormSchema.shape` stays usable. */
export type FormIssue = { kind: 'form'; id: 'storage-share-choice' | 'storage-services'; message: string; selector: string };

export type FieldIssue = {
  kind: 'field';
  field: FieldRuleId;
  selector: string;
  /** The field's value is empty or whitespace. */
  isEmpty: boolean;
  /** What Tab would fill into the empty field for this engine, when anything. */
  defaultValue?: string;
};

export type BlockingIssue = FieldIssue | FormIssue;

function readValue(values: Values, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => (acc && typeof acc === 'object' ? (acc as Record<string, unknown>)[key] : undefined), values);
}

export function formIssues(values: Values): FormIssue[] {
  if (values.engine !== 'azurestorage') return [];
  const issues: FormIssue[] = [];
  if (values.azureStorageShareChoice === 'unset') {
    issues.push({ kind: 'form', id: 'storage-share-choice', message: SHARE_PATH_REQUIRED, selector: '[data-testid="storage-share-path-file"]' });
  }
  if (storageEnabledServices(values).length === 0) {
    issues.push({ kind: 'form', id: 'storage-services', message: STORAGE_SERVICES_REQUIRED, selector: '[data-testid="storage-service-blob"]' });
  }
  return issues;
}

/** The first applicable field whose meta carries an error, in screen order. */
export function firstFieldIssue(fieldMeta: FieldMeta, values: Values): FieldIssue | null {
  for (const rule of FIELD_RULES) {
    if (!rule.applies(values)) continue;
    const errors = fieldMeta[rule.field]?.errors;
    if (!errors || errors.length === 0) continue;
    const value = readValue(values, rule.field);
    const isEmpty = typeof value === 'string' ? value.trim().length === 0 : false;
    const defaultValue = isEmpty && rule.defaultable && values.engine ? getConnectionFieldDefault(values.engine, rule.defaultable) : undefined;
    return { kind: 'field', field: rule.field, selector: rule.selector, isEmpty, ...(defaultValue ? { defaultValue } : {}) };
  }
  return null;
}

export function firstBlockingIssue(fieldMeta: FieldMeta, values: Values): BlockingIssue | null {
  return firstFieldIssue(fieldMeta, values) ?? formIssues(values)[0] ?? null;
}
